import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { debounce } from 'underscore';
import * as R from 'ramda';

import connectToI18n from 'modules/i18n/connectToI18n';
import emptyObject from 'modules/utils/emptyObject';
import { validate, validators } from 'modules/validation';
import { INITIAL_LOADING, READY } from 'modules/utils/ajaxStatuses';
import { userSelector, imperativeEditUser } from 'modules/currentUser';

import { getCountriesAsJSON } from 'models/countries.js';

import ContextService from 'app/scripts/entities/context/context.service';

import { fetchCities } from 'api/cities';
import { updateUser } from 'api/users';

import { isLeaderOfAssembly } from 'models/users.js';

import Modal from 'components/Modal.jsx';
import ProfessionalProfileCompletion from './ProfessionalProfileCompletion.jsx';
export class ProfessionalProfileCompletionContainer extends React.Component {
    static propTypes = {
        trans: PropTypes.func.isRequired,
        user: PropTypes.object.isRequired,
        imperativeEditUser: PropTypes.func.isRequired,
    };

    constructor(props) {
        super(props);
        const { user } = props;

        this.state = {
            completed: false,
            clientErrors: emptyObject,
            serverErrors: emptyObject,
            showErrors: false,
            formData: {
                birthday: user.birthday,
                nationality: user.nationality,
                hasAcceptedSCU: false,
                country: R.path(['address', 'country', 'id'], user),
                zipCode: R.path(['address', 'city', 'zipCode'], user),
                cityId: R.path(['address', 'city', 'id'], user),
                street: R.path(['address', 'street'], user),
            },
            countries: [],
            citiesLoading: true,
            cities: [],
            status: INITIAL_LOADING,
            takeLast: 0,
            isOpen: true,
        };
    }

    componentDidMount() {
        getCountriesAsJSON().then(this.onCountriesReady);
        const country = R.path(['address', 'country', 'id'], this.props.user);
        const zipCode = R.path(['address', 'city', 'zipCode'], this.props.user);
        this.loadCities(country, zipCode);
    }

    onCountriesReady = countries => {
        this.setState({
            countries,
            status: READY,
        });
    };

    loadCities = debounce(function loadCities(country, zipCode) {
        const takeLast = this.state.takeLast + 1;
        const formData = this.state.formData;
        const initialCityId = formData.cityId;
        formData.cityId = null;

        this.setState({
            citiesLoading: true,
            takeLast,
            formData,
        });
        fetchCities({ country, zipCode }).then(data => {
            const cities = data.cities;
            if (takeLast !== this.state.takeLast) {
                return;
            }
            const _formData = this.state.formData;
            if (cities.length === 0) {
                _formData.cityId = null;
            } else if (cities.length === 1) {
                _formData.cityId = cities[0].id;
            } else {
                _formData.cityId = initialCityId || null;
            }
            this.validate(_formData);

            this.setState({
                citiesLoading: false,
                cities,
                formData: _formData,
            });
        });
    }, 300);

    validate = formData => {
        const { assertNotBlank, assertChecked } = validators(this.props.trans);

        const [isValid, errors] = validate.all([
            validate.prop(
                'birthday',
                validate.unless(
                    R.test(/\d{4}-\d{1,2}-\d{1,2}/),
                    this.props.trans('formValidation.type.date')
                )
            ),
            validate.prop('nationality', assertNotBlank),
            validate.prop('street', assertNotBlank),
            validate.prop('cityId', assertNotBlank),
            validate.prop('country', assertNotBlank),
            validate.prop('hasAcceptedSCU', assertChecked),
        ])(formData);

        this.setState({ clientErrors: errors });

        return isValid;
    };

    edit = (field, value) => {
        const formData = { ...this.state.formData };
        formData[field] = value;
        if (field === 'zipCode') {
            this.loadCities(this.state.formData.country, value);
        }

        this.setState({
            formData,
        });

        this.validate(formData);
    };

    submit = () => {
        this.setState({
            showErrors: true,
        });

        if (this.validate(this.state.formData)) {
            this.saveInformation();
        }
    };

    fromFormToModel = formData => {
        return {
            address: {
                street: formData.street,
                city: formData.cityId,
            },
            birthday: formData.birthday,
            nationality: formData.nationality,
            hasAcceptedSCU: formData.hasAcceptedSCU,
        };
    };

    saveInformation = () => {
        updateUser(this.props.user.id, this.fromFormToModel(this.state.formData))
            .then(user => {
                this.props.imperativeEditUser(
                    R.evolve({
                        address: R.always(user.address),
                        birthday: R.always(user.birthday),
                        nationality: R.always(user.nationality),
                        hasAcceptedSCU: R.always(this.state.formData.hasAcceptedSCU),
                    })
                );
                this.setState({ completed: true });
            })
            .catch(response => {
                const jsonResponse = JSON.parse(response.responseText);
                const errors = jsonResponse.detail.errors;
                this.setState({
                    serverErrors: errors,
                    status: READY,
                });
            });
    };

    handleCloseModal = () =>
        this.setState(
            R.evolve({
                isOpen: R.F,
            })
        );

    getCompanyUrl = () => {
        if (this.props.user.isFarmer) {
            return 'producers/company';
        }
        // For hosts, make sure the current context assembly is part of her owns,
        // else take the first hive she is leader of.
        let assemblyId;
        const currentAssemblyId = ContextService.getCurrentContext().get('data').hiveId;
        if (isLeaderOfAssembly({ user: this.props.user, assemblyId: currentAssemblyId })) {
            assemblyId = currentAssemblyId;
        } else {
            assemblyId = R.path(['hivesAsLeader', '0', 'id'], this.props.user);
        }
        return `assemblies/${assemblyId}/company`;
    };

    render() {
        return (
            <Modal
                isOpened={this.state.isOpen}
                isBlocking
                closeModal={this.handleCloseModal}
                className="kycProfessionalCompletionModal"
            >
                <ProfessionalProfileCompletion
                    {...this.state}
                    edit={this.edit}
                    submit={this.submit}
                    closeModal={this.handleCloseModal}
                    companyUrl={this.state.completed ? this.getCompanyUrl() : ''}
                    supportUrl={`support/pro-section-improvements-${
                        this.props.user.isFarmer ? 'prod' : 'host'
                    }`}
                />
            </Modal>
        );
    }
}

const mapStateToProps = state => ({
    user: userSelector(state),
});

export default connect(mapStateToProps, { imperativeEditUser })(
    connectToI18n(ProfessionalProfileCompletionContainer)
);
